import { uploadData, getUrl, getProperties } from 'aws-amplify/storage';
import { v4 as uuidv4 } from 'uuid';
import yaml from 'js-yaml';
import { S3File } from 'features/file-browser/state/fileAtoms';
import { Parameter } from 'features/morphing/state/parameterAtoms';
import { alertAuthExpired, getFfdUrl } from 'shared/api/ffdClient';

export const submitMorphingStart = async (
  displayModel: S3File | null,
  authToken: string,
  cognitoId: string,
  sp: Parameter,
  morphingType: string
): Promise<S3File | null> => {
  if (!displayModel || null) {
    alert('モーフィングする車体モデルを選択し、描画して下さい。');
    return null;
  }

  // モーフィングパラメータの上限と下限どちらで実施するか
  let set_parameter = 0;
  if (morphingType === 'upper') {
    set_parameter = sp.morphingParameter.upper;
  } else if (morphingType === 'lower') {
    set_parameter = sp.morphingParameter.lower;
  } else {
    return null;
  }

  // Add the second element for y-symmetry if ySymmetry is true
  const targetControlPoints = [
    {
      x: [sp.targetControlPointsX.start - 1, sp.targetControlPointsX.stop],
      y: [sp.targetControlPointsY.start - 1, sp.targetControlPointsY.stop],
      z: [sp.targetControlPointsZ.start - 1, sp.targetControlPointsZ.stop],
    }
  ];

  if (sp.ySymmetry) {
    targetControlPoints.push({
      x: [sp.targetControlPointsX.start - 1, sp.targetControlPointsX.stop],
      y: [
        sp.controlPoints.y - sp.targetControlPointsY.stop,
        sp.controlPoints.y - sp.targetControlPointsY.start + 1,
      ],
      z: [sp.targetControlPointsZ.start - 1, sp.targetControlPointsZ.stop],
    });
  }

  // YAML ファイルの内容を作成
  const config = {
    s3path: displayModel.path,
    name: sp.name,
    bound: set_parameter,
    control_points: [
      sp.controlPoints.x,
      sp.controlPoints.y,
      sp.controlPoints.z
    ],
    bbox_origin: [
      sp.morphingMin.x,
      sp.morphingMin.y,
      sp.morphingMin.z
    ],
    bbox_length: [
      sp.morphingMax.x - sp.morphingMin.x,
      sp.morphingMax.y - sp.morphingMin.y,
      sp.morphingMax.z - sp.morphingMin.z
    ],
    direction: sp.morphingDirection,
    y_symmetry: sp.ySymmetry,
    target_control_points: targetControlPoints
  };

  // YAML形式に変換
  const yamlData = yaml.dump(config);

  // ランダムなファイル名を生成
  const yamlFileName = `${uuidv4()}.yml`;
  const yamlFilePath = `from-web-app/${cognitoId}/ffd-config/${yamlFileName}`;

  // S3にYAMLファイルをアップロード
  try {
    const task = uploadData({
      path: yamlFilePath,
      data: yamlData
    });
    await task.result;
  } catch (error) {
    alert("Failed to upload .YAML to the server.");
  }

  // Send API request using uploaded YAML file
  try {
    const response = await fetch(getFfdUrl("/morphing/start"), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': authToken,
      },
      body: JSON.stringify({ yamlFilePath }),
    });

    const data = await response.json();
    if (!response.ok) {
      alert(data.text);
      return null;
    }

    // On success, set the newly generated .stl to Base Model
    const newPath = data.body as string;
    const newName = newPath.split('/').pop() || '';
    const newType = newName.split('.').pop() || '';

    const linkToStorageFile = await getUrl({
      path: newPath,
      options: {
        validateObjectExistence: false,
        expiresIn: 1800,
      },
    });

    const properties = await getProperties({
      path: newPath,
    });

    return {
      name: newName,
      type: newType,
      lastModified: properties.lastModified || new Date(0),
      size: (properties.size as number / 1048576).toFixed(1) + 'MB',
      path: newPath,
      url: linkToStorageFile.url.toString(),
    } as S3File;
  } catch (error) {
    alertAuthExpired();
    return null;
  }
};
